import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Space,
  Button,
  Tag,
  Progress,
  Tabs,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Spin,
  Alert,
  message,
  InputNumber
} from 'antd';
import {
  TeamOutlined,
  UserAddOutlined,
  DollarOutlined,
  PlusOutlined,
  ApartmentOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  employeeService,
  departmentService,
  positionService,
  Employee,
  Department,
  Position,
  EmployeeStats,
  DepartmentStats,
  PositionStats,
  CreateEmployeeInput
} from '../../../services/hrService';
import EmployeeManagement from '../components/EmployeeManagement';
import LeaveManagement from '../components/LeaveManagement';
import AttendanceManagement from '../components/AttendanceManagement';
import PayrollManagement from '../components/PayrollManagement';
import CompanySwitcher from '@/components/CompanySwitcher';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

// Color palette for department distribution
const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa541c'];

const HROverview: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Function to get active tab from route
  const getActiveTabFromRoute = () => {
    const path = location.pathname;
    if (path.includes('/hr/employees')) return 'employees';
    if (path.includes('/hr/payroll')) return 'payroll';
    if (path.includes('/hr/attendance')) return 'attendance';
    return 'overview';
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromRoute());
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [employeeStats, setEmployeeStats] = useState<EmployeeStats | null>(null);
  const [departmentStats, setDepartmentStats] = useState<DepartmentStats | null>(null);
  const [positionStats, setPositionStats] = useState<PositionStats | null>(null);
  const [newHiresCount, setNewHiresCount] = useState<number>(0);
  const [terminationsCount, setTerminationsCount] = useState<number>(0);
  const [averageSalary, setAverageSalary] = useState<number>(0);

  const [isEmployeeModalVisible, setIsEmployeeModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form] = Form.useForm();

  // Update active tab when route changes
  useEffect(() => {
    setActiveTab(getActiveTabFromRoute());
  }, [location.pathname]);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [empData, deptData, posData] = await Promise.all([
        employeeService.getAll(),
        departmentService.getAll(),
        positionService.getAll()
      ]);
      setEmployees(empData || []);
      setDepartments(deptData || []);
      setPositions(posData || []);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Failed to load HR data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [empStats, deptStats, posStats, newHires, terminations, avgSalary] = await Promise.all([
        employeeService.getStats(),
        departmentService.getStats(),
        positionService.getStats(),
        employeeService.getNewHiresCount(),
        employeeService.getTerminationsCount(),
        employeeService.getAverageSalary()
      ]);
      setEmployeeStats(empStats);
      setDepartmentStats(deptStats);
      setPositionStats(posStats);
      setNewHiresCount(newHires);
      setTerminationsCount(terminations);
      setAverageSalary(avgSalary);
    } catch (err: any) {
      console.error('Error fetching stats:', err);
      // Don't show error for stats - just use defaults
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [fetchData, fetchStats]);

  const handleAddEmployee = async (values: any) => {
    setLoading(true);
    try {
      const employeeData: CreateEmployeeInput = {
        employeeId: values.employeeId,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        departmentId: values.departmentId,
        positionId: values.positionId,
        hireDate: values.hireDate?.format('YYYY-MM-DD') || new Date().toISOString().split('T')[0],
        salary: values.salary || 0,
        employmentType: values.employmentType,
        status: 'ACTIVE',
        isActive: true,
      };

      await employeeService.create(employeeData);
      message.success('Employee added successfully');
      setIsEmployeeModalVisible(false);
      form.resetFields();
      fetchData();
      fetchStats();
    } catch (error: any) {
      console.error('Error adding employee:', error);
      message.error(error.response?.data?.message || 'Failed to add employee');
    } finally {
      setLoading(false);
    }
  };

  // Calculate department distribution for chart
  const getDepartmentDistribution = () => {
    if (!employeeStats?.byDepartment) return [];

    const total = employeeStats.total || 1;
    return employeeStats.byDepartment
      .filter(d => d.departmentName)
      .map((d, index) => ({
        name: d.departmentName || 'Unknown',
        count: d.count,
        percentage: ((d.count / total) * 100).toFixed(1),
        color: COLORS[index % COLORS.length]
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6); // Top 6 departments
  };

  const renderOverview = () => (
    <div>
      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable>
            <Statistic
              title="Total Employees"
              value={employeeStats?.total || employees.length}
              prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
              loading={statsLoading}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                Active: {employeeStats?.active || 0}
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable>
            <Statistic
              title="Departments"
              value={departmentStats?.total || departments.length}
              prefix={<ApartmentOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
              loading={statsLoading}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                Active: {departmentStats?.active || 0}
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable>
            <Statistic
              title="New Hires (30 days)"
              value={newHiresCount}
              prefix={<UserAddOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
              loading={statsLoading}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                Terminations: {terminationsCount}
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable>
            <Statistic
              title="Average Salary"
              value={averageSalary}
              prefix={<DollarOutlined style={{ color: '#722ed1' }} />}
              precision={0}
              valueStyle={{ color: '#722ed1' }}
              loading={statsLoading}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                Positions: {positionStats?.total || positions.length}
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Department Distribution & Recent Activity */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title="Department Distribution"
            style={{ height: 400 }}
            extra={
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={fetchStats}
                loading={statsLoading}
              />
            }
          >
            {statsLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {getDepartmentDistribution().length > 0 ? (
                  getDepartmentDistribution().map((dept, index) => (
                    <div key={index}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span>{dept.name}</span>
                        <span>{dept.count} ({dept.percentage}%)</span>
                      </div>
                      <Progress
                        percent={Number(dept.percentage)}
                        strokeColor={dept.color}
                        showInfo={false}
                      />
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Text type="secondary">No department data available</Text>
                  </div>
                )}
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title="Employee Status Distribution"
            style={{ height: 400 }}
          >
            {statsLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {employeeStats?.byStatus?.length ? (
                  employeeStats.byStatus.map((item, index) => {
                    const total = employeeStats.total || 1;
                    const percentage = ((item.count / total) * 100).toFixed(1);
                    const color = item.status === 'ACTIVE' ? '#52c41a' :
                                  item.status === 'TERMINATED' ? '#f5222d' :
                                  item.status === 'ON_LEAVE' ? '#faad14' : '#8c8c8c';
                    return (
                      <div key={index}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                          <Tag color={color}>{item.status}</Tag>
                          <span>{item.count} ({percentage}%)</span>
                        </div>
                        <Progress
                          percent={Number(percentage)}
                          strokeColor={color}
                          showInfo={false}
                        />
                      </div>
                    );
                  })
                ) : (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Text type="secondary">No status data available</Text>
                  </div>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Quick Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Employment Types">
            {employeeStats?.byEmploymentType?.length ? (
              <Row gutter={[16, 16]}>
                {employeeStats.byEmploymentType.map((item, index) => (
                  <Col span={8} key={index}>
                    <Statistic
                      title={item.employmentType || 'Not Set'}
                      value={item.count}
                      valueStyle={{ fontSize: 20 }}
                    />
                  </Col>
                ))}
              </Row>
            ) : (
              <Text type="secondary">No employment type data</Text>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Position Levels">
            {positionStats?.byLevel?.length ? (
              <Row gutter={[16, 16]}>
                {positionStats.byLevel.slice(0, 6).map((item, index) => (
                  <Col span={8} key={index}>
                    <Statistic
                      title={item.level}
                      value={item.count}
                      valueStyle={{ fontSize: 20 }}
                    />
                  </Col>
                ))}
              </Row>
            ) : (
              <Text type="secondary">No position level data</Text>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Error Loading HR Data"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={fetchData}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>Human Resources</Title>
        <Space>
          <CompanySwitcher />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => { fetchData(); fetchStats(); }}
            loading={loading || statsLoading}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsEmployeeModalVisible(true)}
          >
            Add Employee
          </Button>
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Overview" key="overview">
          {renderOverview()}
        </TabPane>

        <TabPane tab="Employees" key="employees">
          <EmployeeManagement />
        </TabPane>

        <TabPane tab="Attendance" key="attendance">
          <AttendanceManagement />
        </TabPane>

        <TabPane tab="Leave Management" key="leaves">
          <LeaveManagement />
        </TabPane>

        <TabPane tab="Payroll" key="payroll">
          <PayrollManagement />
        </TabPane>
      </Tabs>

      {/* Add Employee Modal */}
      <Modal
        title="Add New Employee"
        open={isEmployeeModalVisible}
        onCancel={() => {
          setIsEmployeeModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleAddEmployee}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="employeeId"
                label="Employee ID"
                rules={[{ required: true, message: 'Please enter employee ID' }]}
              >
                <Input placeholder="e.g., EMP001" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: 'Please enter first name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: 'Please enter last name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="Phone">
                <Input placeholder="+852 1234 5678" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="departmentId" label="Department">
                <Select placeholder="Select department" allowClear>
                  {departments.map(dept => (
                    <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="positionId" label="Position">
                <Select placeholder="Select position" allowClear>
                  {positions.map(pos => (
                    <Option key={pos.id} value={pos.id}>{pos.title}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="salary"
                label="Annual Salary"
                rules={[{ required: true, message: 'Please enter salary' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/\$\s?|(,*)/g, '') as unknown as number}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="hireDate"
                label="Hire Date"
                rules={[{ required: true, message: 'Please select hire date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="employmentType" label="Employment Type">
                <Select placeholder="Select type">
                  <Option value="FULL_TIME">Full Time</Option>
                  <Option value="PART_TIME">Part Time</Option>
                  <Option value="CONTRACT">Contract</Option>
                  <Option value="INTERN">Intern</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setIsEmployeeModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Add Employee
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HROverview;
